import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Box, Paper, Stack, Typography, CircularProgress } from '@mui/material';
import { fetchGcsBlobByPrefix } from '../../../services/api_utils';
import { tableScope } from '../../../utils/blobPaths';

// The storage family of a document's page images, as functions/common/blob_paths.py names it.
const DOCUMENT_ASSETS = 'document_assets';

const PAGE_WINDOW_SIZE = 20;
const PAGE_WINDOW_STEP = 5;
const FORWARD_PAGE_INDEX = 11; // Begin prefetching when the twelfth loaded page becomes visible.

function DocumentPage({ pageNum, index, prefix, active, washed, onVisible }) {
  const pageRef = useRef(null);
  const [image, setImage] = useState({ url: null, failed: false });
  const [aspectRatio, setAspectRatio] = useState('1 / 1.414');

  useEffect(() => {
    let watching = true;
    const observer = new IntersectionObserver((entries) => {
      if (watching && entries.some((entry) => entry.isIntersecting)) onVisible(index);
    });
    observer.observe(pageRef.current);
    return () => { watching = false; observer.disconnect(); };
  }, [index, onVisible]);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    // A page the file's page map does not name was never rendered.
    setImage({ url: null, failed: !prefix });
    if (!active || !prefix) return undefined;

    (async () => {
      try {
        const blob = await fetchGcsBlobByPrefix(prefix);
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setImage({ url: objectUrl, failed: false });
      } catch (error) {
        if (cancelled) return;
        console.error(`Failed to load document page ${pageNum}:`, error);
        setImage({ url: null, failed: true });
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [active, prefix, pageNum]);

  return (
    <Paper
      ref={pageRef}
      data-page-index={index}
      variant="outlined"
      // Pages outside the focused table recede; the table's own stay lit.
      sx={{ overflow: 'hidden', opacity: washed ? 0.45 : 1, transition: 'opacity 0.2s' }}
    >
      {/* The page shell survives eviction. Its measured aspect ratio also
          survives, so removing an image never moves the reader's position. */}
      <Box role="group" aria-label={`Page ${pageNum} image area`} style={{ aspectRatio }} sx={{
        bgcolor: 'action.hover', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
      }}>
        {active && image.url && (
          <Box
            component="img"
            src={image.url}
            alt={`Page ${pageNum}`}
            onLoad={(event) => {
              const { naturalWidth, naturalHeight } = event.currentTarget;
              if (naturalWidth && naturalHeight) setAspectRatio(`${naturalWidth} / ${naturalHeight}`);
            }}
            sx={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain' }}
          />
        )}
        {active && !image.url && (image.failed ? (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ p: 2 }}>
            This page could not be loaded.
          </Typography>
        ) : <CircularProgress size={24} />)}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{
        display: 'block', px: 1, py: 0.5, borderTop: 1, borderColor: 'divider',
      }}>
        Page {pageNum}
      </Typography>
    </Paper>
  );
}

/**
 * Every page of a document file, from the page images its file document names
 * (`files/{id}.assets.pages`: page number -> image path, and `page_count`).
 * Keep twenty images around the reader, shifting by five in either direction.
 * Page shells remain mounted so native scrolling and thumb jumps still work.
 *
 * `focusRange` ([first, last], or null) lifts one table's pages: the others are
 * washed, and the range's first page is scrolled to whenever the focus moves.
 *
 * A page path read off the file document is followed only inside that file's own
 * document-assets folder, so a document naming another file's or another
 * tenant's blob shows the page as unavailable.
 */
const DocumentPagePane = ({ companyId, tableId, fileId, pages, pageCount, focusRange = null }) => {
  const [windowState, setWindowState] = useState({ fileId, start: 0 });
  // A window remembered for another document starts again from its first page.
  const windowStart = windowState.fileId === fileId ? windowState.start : 0;
  const containerRef = useRef(null);
  const focusFirst = Array.isArray(focusRange) ? focusRange[0] : null;
  const focusLast = Array.isArray(focusRange) ? focusRange[1] : null;

  const pageNums = useMemo(
    () => Array.from({ length: pageCount || 0 }, (_, index) => index + 1),
    [pageCount],
  );
  const pageFolder = `${tableScope(companyId, tableId)}/${DOCUMENT_ASSETS}/${fileId}/`;
  const ownedPage = (path) => (
    typeof path === 'string' && path.startsWith(pageFolder) && !path.split('/').includes('..')
  );

  const onVisible = useCallback((index) => {
    setWindowState((previous) => {
      const start = previous.fileId === fileId ? previous.start : 0;
      const lastStart = Math.max(0, pageNums.length - PAGE_WINDOW_SIZE);
      if (index >= start + FORWARD_PAGE_INDEX) {
        // A scrollbar jump goes straight to the required window rather than
        // downloading every intermediate group of pages.
        const steps = Math.floor((index - start - FORWARD_PAGE_INDEX) / PAGE_WINDOW_STEP) + 1;
        return { fileId, start: Math.min(lastStart, start + steps * PAGE_WINDOW_STEP) };
      }
      if (index < start + PAGE_WINDOW_STEP) {
        const steps = Math.ceil((start + PAGE_WINDOW_STEP - index) / PAGE_WINDOW_STEP);
        return { fileId, start: Math.max(0, start - steps * PAGE_WINDOW_STEP) };
      }
      return { fileId, start };
    });
  }, [fileId, pageNums.length]);

  // Bring the focused table's first page into view; its visibility then moves
  // the window so the page loads.
  useEffect(() => {
    if (!focusFirst || !containerRef.current) return;
    const page = containerRef.current.querySelector(`[data-page-index="${focusFirst - 1}"]`);
    if (page) page.scrollIntoView({ block: 'start' });
  }, [fileId, focusFirst]);

  if (pageNums.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        No pages were rendered for this document.
      </Typography>
    );
  }

  return (
    <Stack ref={containerRef} spacing={2}>
      {pageNums.map((pageNum, index) => (
        <DocumentPage
          key={`${fileId}/${pageNum}`}
          pageNum={pageNum}
          index={index}
          prefix={ownedPage(pages[String(pageNum)]) ? pages[String(pageNum)] : null}
          active={index >= windowStart && index < windowStart + PAGE_WINDOW_SIZE}
          washed={focusFirst !== null && (pageNum < focusFirst || pageNum > focusLast)}
          onVisible={onVisible}
        />
      ))}
    </Stack>
  );
};

export default DocumentPagePane;
